import React, { useState, useEffect, FunctionComponent } from "react";
import CreatableSelect, { Props } from "react-select/creatable";
import { getIn, FieldProps } from "formik";
import { emphasize } from "@material-ui/core/styles/colorManipulator";
import FormHelperText from "@material-ui/core/FormHelperText";
import { makeStyles, Theme } from "@material-ui/core/styles";

import {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
} from "./components";

const useStyles = makeStyles(({ spacing, palette, brand }: Theme) => ({
  input: (props: { margin: string }) => ({
    display: "flex",
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: props.margin === "dense" ? "37.63px" : "53.63px",
    margin: 0,
    height: "auto",
  }),
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
    lineHeight: "0.5rem",
    padding: spacing(1, 0),
  },
  chip: {
    height: 26,
    color: brand.stormyBlue[100],
    background: "#E3E8EF",
    margin: spacing(0.5, 0.25),
  },
  chipFocused: {
    backgroundColor: emphasize(
      palette.type === "light" ? palette.grey[300] : palette.grey[700],
      0.08
    ),
  },
  chipInvalid: {
    background: "#FED6DC",
  },
  noOptionsMessage: {
    padding: spacing(1, 2),
  },
  singleValue: {},
  placeholder: {
    position: "absolute",
  },
  helperText: {
    margin: spacing(1, 0),
  },
}));

type ICreatableFieldProps = Props<any>;
type CreatableFieldFormikProps = ICreatableFieldProps & FieldProps;

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  DropdownIndicator: null,
  IndicatorSeparator: null,
};

const mapValues = (values: any, errors: string[]) =>
  values.map((value: any, index: number) => ({
    label: value,
    value,
    isInvalid: Boolean(errors[index]),
  }));

const getRawValues = (values: Array<any>) => values.map(({ value }) => value);

export const CreatableField: FunctionComponent<ICreatableFieldProps> = ({
  isClearable = true,
  isMulti = true,
  disabled = false,
  placeholder = "",
  margin = "dense",
  ...props
}) => {
  const customClasses = useStyles({ margin });
  const {
    field,
    form,
    ...selectFieldProps
  } = props as CreatableFieldFormikProps;
  const isFormikConnected = form && field;
  const [values, setValues] = useState<any>([]);
  const [inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    if (field.value) {
      setValues(field.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isFormikConnected) {
    return null;
  }

  const handleKeyDown = (event: any) => {
    switch (event.key) {
      case "Enter":
      case "Tab":
      case ",":
      case "Spacebar":
      case " ":
        const newValues = [...values, inputValue];
        setInputValue("");
        setValues(newValues);
        form.setFieldValue(field.name, newValues);
        event.preventDefault();
    }
  };

  const handleInputChange = (inputValue = "") => setInputValue(inputValue);

  const handleValueChange = (values: any) => {
    const rawValues = getRawValues(values);
    form.setFieldValue(field.name, rawValues);
    setValues(rawValues);
  };

  const handleOnPaste = (event: any) => {
    event.preventDefault();
    const clipboard = event.clipboardData.getData("Text");

    if (!clipboard) {
      return;
    }

    const commaSeparatedValues = clipboard
      .split(",")
      .map((value: string) => value.trim());
    const newValues = [...values, ...commaSeparatedValues];

    form.setFieldValue(field.name, newValues);
    setValues(newValues);
  };

  const fieldError: string | string[] = getIn(form.errors, field.name);
  const showError = getIn(form.touched, field.name) && Boolean(fieldError);
  const errorMessage = Array.isArray(fieldError)
    ? fieldError.filter((e) => Boolean(e))[0]
    : fieldError;
  const errors = Array.isArray(fieldError) ? fieldError : [];

  return (
    <div onPaste={handleOnPaste}>
      <CreatableSelect
        classes={customClasses}
        components={components}
        isClearable={isClearable}
        isMulti={isMulti}
        menuIsOpen={false}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onChange={handleValueChange}
        onInputChange={handleInputChange}
        isDisabled={disabled}
        showError={showError}
        margin={margin}
        value={mapValues(values, errors)}
        inputValue={inputValue}
        {...selectFieldProps}
      />
      {showError && (
        <FormHelperText error className={customClasses.helperText}>
          {errorMessage}
        </FormHelperText>
      )}
    </div>
  );
};
